import { ReactNode, Suspense, lazy, useId } from "react";
import { twMerge } from "tailwind-merge";

import { MentionIdentityTooltip } from "./MentionIdentityTooltip";
import { type MentionTarget, containsMention, decorateMentionsInHtml } from "./mentions";

import { LoadingDotsIcon } from "mds/icons";

const Markdown = lazy(() => import("mds/components/Markdown"));

export type ChatBubbleProps = {
  className?: string;
  children?: ReactNode;
  header?: ReactNode;
  isSelf?: boolean; // Optional prop to indicate if the message is from the current user
  mentionTargets?: MentionTarget[];
  messageId?: string;
  parseMarkdown?: boolean;
};

export const ChatBubble = function ChatBubbleInternal({
  ref,
  className,
  header,
  children,
  isSelf = false,
  mentionTargets = [],
  messageId,
  parseMarkdown = false,
}: ChatBubbleProps & { ref?: React.Ref<HTMLDivElement> }) {
  const mentionTooltipScopeId = useId();
  const transformMarkdownHtml = (html: string) =>
    decorateMentionsInHtml(html, mentionTargets, mentionTooltipScopeId);

  // We markdown-transform relevant child elements
  let processedChildren = children || "";

  const childrenList = Array.isArray(children) ? children : [children];
  const stringChildren = childrenList
    .filter((child): child is string => typeof child === "string")
    // The AI response will sometimes insert literal `\n` to indicate newlines,
    // which we'll resolve here.
    .map((s) => s.replace("\\n", "\n"));
  if (parseMarkdown) {
    const nonStringChildren = childrenList.filter((child) => typeof child !== "string");
    processedChildren = (
      <Suspense fallback={<LoadingDotsIcon />}>
        {stringChildren.map((child, index) => (
          // The position identifies this Markdown child while its streamed text changes.
          <Markdown
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            markdown={child}
            transformHtml={mentionTargets.length ? transformMarkdownHtml : undefined}
          />
        ))}
        {nonStringChildren}
      </Suspense>
    );
  }
  const matchedMentionTargets = mentionTargets.filter((target) =>
    stringChildren.some((text) => containsMention(text, target.mentionText)),
  );
  const mentionedUsers = matchedMentionTargets.filter(
    (target): target is Extract<MentionTarget, { kind: "user" }> => target.kind === "user",
  );

  return (
    <div
      className={twMerge(
        "flex min-w-0 max-w-full flex-col rounded-xl p-4",
        isSelf
          ? "ml-6 self-end rounded-tr-none bg-blue-tint-95"
          : "mr-6 self-start rounded-tl-none bg-black-tint-97",
        className,
      )}
      data-chat-message-id={messageId}
      ref={ref}
    >
      {header}
      <div
        className={twMerge(
          "min-w-0 overflow-y-hidden whitespace-pre-wrap break-words text-sm font-normal text-black-tint-20",
          matchedMentionTargets.length && "-my-1 -ml-1 py-1 pl-1",
        )}
      >
        {processedChildren}
      </div>
      {mentionedUsers.map((target) => (
        <MentionIdentityTooltip key={target.id} scopeId={mentionTooltipScopeId} target={target} />
      ))}
    </div>
  );
};
